import { useState } from "react";

import { MediaImage } from "@/components/ui/MediaImage";
import type { MediaItem } from "@/data/media";
import { parseVideoUrl, type VideoSource } from "@/lib/video";

export { parseVideoUrl };
export type { VideoSource };

/**
 * A 16:9 video area that behaves correctly when there is no video.
 *
 * Three states, and the empty one is the one worth explaining.
 *
 * No URL: the block renders its cover photograph, full bleed, with nothing on
 * it. No play button, because there is nothing to play; no caption announcing
 * that a film is coming, because that is a promise the organisation has not
 * made. It reads as a photograph in a 16:9 frame, which is what it is.
 *
 * It does NOT render an iframe with an empty src. An empty iframe is a
 * same-origin copy of the current page inside the current page, which in a
 * server-rendered site means a second full render, a second set of requests,
 * and a layout that behaves strangely for anybody using a screen reader. The
 * internal placeholder string is treated as "no URL" too, so a section can be
 * staged before HISCUS supplies the real address.
 *
 * URL, not yet played: the cover photograph with a familiar host-specific play
 * button over it, and nothing loaded from the video host. The privacy behaviour
 * does not need to be narrated inside the artwork; it is explained on the
 * cookie page and kept here by loading the iframe only after the click.
 *
 * Playing: the embed, with `autoplay=1` because the visitor has just asked for
 * it by clicking, and never with sound arriving unasked on page load.
 */

export function VideoBlock({
  url,
  poster,
  title,
  emptyNote = "",
  className = "",
  radius = 24,
}: {
  url?: string | null;
  poster?: MediaItem | null;
  title: string;
  emptyNote?: string;
  className?: string;
  radius?: number;
}) {
  const source = parseVideoUrl(url);
  const [playing, setPlaying] = useState(false);

  return (
    <div
      className={`relative aspect-video w-full overflow-hidden bg-midnight ${className}`}
      style={{ borderRadius: radius }}
    >
      {playing && source ? (
        <iframe
          src={source.embedUrl}
          title={title}
          // No allow="autoplay" beyond what the click already granted, and no
          // fullscreen-by-default surprises: this is the minimum a player needs.
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
          className="absolute inset-0 h-full w-full border-0"
        />
      ) : (
        <>
          {poster ? (
            <MediaImage
              item={poster}
              fill
              sizes="(min-width: 1024px) 46vw, 100vw"
              className="object-cover"
            />
          ) : null}
          {/*
            With only a play button over the photograph, a light wash gives the
            control enough contrast without making the thumbnail look gloomy.
          */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background: source
                ? "linear-gradient(180deg, rgba(12,29,51,0.08) 0%, rgba(12,29,51,0.04) 48%, rgba(12,29,51,0.22) 100%)"
                : emptyNote
                  ? "linear-gradient(180deg, rgba(12,29,51,0.06) 0%, rgba(12,29,51,0.10) 45%, rgba(12,29,51,0.62) 100%)"
                  : "linear-gradient(180deg, rgba(12,29,51,0.04) 0%, rgba(12,29,51,0.08) 100%)",
            }}
          />

          {source ? (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label={`Play ${title}${source.kind === "youtube" ? " on YouTube" : ""}`}
              className="group absolute inset-0 flex items-center justify-center text-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-white/90"
            >
              {source.kind === "youtube" ? (
                <span className="flex h-14 w-20 items-center justify-center rounded-[14px] bg-[#ff0033] shadow-[0_8px_28px_rgba(0,0,0,0.32)] transition-[background-color,transform,box-shadow] duration-300 ease-out group-hover:scale-105 group-hover:bg-[#e6002e] group-hover:shadow-[0_10px_34px_rgba(0,0,0,0.42)] group-active:scale-95 sm:h-16 sm:w-[5.75rem]">
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-current sm:h-8 sm:w-8">
                    <path d="M8 5.5v13l11-6.5z" />
                  </svg>
                </span>
              ) : (
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/50 bg-black/55 shadow-[0_8px_28px_rgba(0,0,0,0.28)] backdrop-blur-sm transition-[background-color,transform] duration-300 ease-out group-hover:scale-105 group-hover:bg-black/70 group-active:scale-95">
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-current">
                    <path d="M8 5.5v13l11-6.5z" />
                  </svg>
                </span>
              )}
            </button>
          ) : (
            /*
             * No URL yet: the photograph, and nothing else.
             *
             * No dead play button, because a control that does nothing is worse
             * than no control, and no standing caption, because the block
             * should not draw attention to what it is waiting for. The field is
             * waiting in the admin panel, and this becomes a player the moment
             * somebody pastes an address into it.
             */
            emptyNote ? (
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <p className="max-w-md text-[0.875rem] text-white/80">{emptyNote}</p>
              </div>
            ) : null
          )}
        </>
      )}
    </div>
  );
}
